import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Field,
  Flex,
  Input,
  InputGroup,
  NativeSelect,
  Portal,
  Tag,
  Text,
} from '@chakra-ui/react';
import type { PortfolioType } from '@customTypes/portfolio';
import type { UserType } from '@customTypes/user';
import type { FormErrors } from '@customTypes/errors';
import { capitalizeFirstLetter } from '@utils/formats';
import { toast } from 'react-toastify';
import { datadogRum } from '@datadog/browser-rum';
import api from '@services/api';
import ErrorLabel from '@components/ErrorLabel';
import useGetPortfolioList from '@hooks/useGetPortfolioList';
import useGetUsersList from '@hooks/useGetUsersList';
import useGetCustomerTags from '@hooks/useGetCustomerTags';
import PortfolioSelect from '@components/PortfolioSelect';
import UserSelect from '@components/UserSelect';
import CustomDialogBackdrop from '@components/CustomDialogBackdrop';

interface Props {
  handleClose: () => void;
  afterSubmit: () => void;
};

const AddDialog = ({ handleClose, afterSubmit }: Props) => {
  const {
    getPortfolios,
    portfolios,
  } = useGetPortfolioList();

  const {
    getUsers,
    users,
  } = useGetUsersList();

  const {
    customerTags,
    getTags,
    loading: loadingTags,
  } = useGetCustomerTags();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors | null>(null);
  const [selectedPortfolio, setSelectedPortfolio] = useState<PortfolioType | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<UserType[]>([]);
  const [tags, setTags] = useState<string[]>([]);

  useEffect(() => {
    getPortfolios({});
    getUsers({});
  }, [getPortfolios, getUsers]);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedPortfolio) {
      setErrors({ portfolio: ['Selecione um portfólio'] });
      return;
    }

    const formData = new FormData(event.currentTarget);
    const data = Object.fromEntries(formData.entries());

    setLoading(true);

    const params = {
      name: capitalizeFirstLetter(data.name as string),
      portfolio_id: selectedPortfolio.id,
      tags,
      users: selectedUsers.map((user) => user.id),
    };

    try {
      const response = await api.post('/campaign', {
        ...params,
      });

      if (response?.status === 200) {
        toast.success('Campanha criada com sucesso!');
        afterSubmit();
        handleClose();
      } else if (response?.data?.errors) {
        setErrors(response.data.errors);
      }
    } catch (error) {
      toast.error('Erro ao criar a campanha. Verifique os dados e tente novamente.');
      datadogRum.addError(new Error('Fail to create campaign'), {
        error,
        params,
      });
    } finally {
      setLoading(false);
    };
  };

  const handleWithSelectPortfolio = async (portfolio: PortfolioType | null) => {
    setSelectedPortfolio(portfolio);
    if (portfolio) {
      await getTags(portfolio.id);
    }
  };

  const handleWithSelectUser = async (user: UserType | null) => {
    if (!user) return;
    if (selectedUsers.find((u) => u.id === user.id)) return;

    setSelectedUsers((prev) => [...prev, user])
  };

  const tagsList = customerTags.filter((tag) => !tags.includes(tag));

  const handleRemoveTag = (currentTag: string) => {
    setTags((prev) => prev.filter((tag) => tag !== currentTag));
  };

  const handleRemoveUser = (userId: string) => {
    setSelectedUsers((prev) => prev.filter((user) => user.id !== userId));
  };

  return (
    <Dialog.Root size="md" placement="center" open onOpenChange={handleClose} closeOnInteractOutside={false}>
      <Portal>
        <CustomDialogBackdrop />
        <Dialog.Positioner>
          <Dialog.Content bg="grayscale.fg">
            <Dialog.Header>
              <Dialog.Title>Nova campanha</Dialog.Title>
            </Dialog.Header>

            <form onSubmit={onSubmit}>
              <Dialog.Body>
                <Field.Root invalid={!!errors?.name}>
                  <Field.Label>Nome</Field.Label>
                  <InputGroup width="full" gap={0}>
                    <Input name="name" placeholder="Digite aqui..." minLength={3} variant="subtle" required />
                  </InputGroup>
                  {errors?.name?.map((error, index) => (
                    <Field.ErrorText key={index}>{error}</Field.ErrorText>
                  ))}
                </Field.Root>
                <Box my={4}>
                  <PortfolioSelect
                    errors={errors}
                    portfolios={portfolios}
                    onSearch={async (text) => await getPortfolios({ query: text })}
                    value={selectedPortfolio}
                    onChange={handleWithSelectPortfolio}
                  />
                  {errors?.portfolio?.map((error, index) => (
                    <ErrorLabel key={index} text={error} />
                  ))}
                </Box>
                {selectedPortfolio && (
                  <Box mb={4}>
                    <Text fontWeight={500} mb={1}>Tags</Text>
                    {loadingTags && (
                      <Text color="fg.muted" fontSize="xs" mb={1}>Carregando listagem...</Text>
                    )}
                    {tagsList.length > 0 && (
                      <>
                        <NativeSelect.Root size="md" variant="subtle" invalid={!!errors?.tags}>
                          <NativeSelect.Field
                            placeholder="Selecione uma tag"
                            value=""
                            onChange={(e) => setTags((prev) => [...prev, e.target.value])}
                          >
                            {tagsList.map((tag) => (
                              <option key={tag} value={tag}>
                                {tag}
                              </option>
                            ))}
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                        {errors?.tags?.map((error, index) => (
                          <ErrorLabel key={index} text={error} />
                        ))}
                      </>
                    )}
                    {tags.length > 0 && (
                      <Flex gap={1} flexWrap="wrap" mt={2}>
                        {tags.map((tag) => (
                          <Tag.Root key={tag} size="lg" bg="argos.fg" color="grayscale" fontWeight={600}>
                            <Tag.Label>{tag}</Tag.Label>
                            <Tag.EndElement>
                              <Tag.CloseTrigger
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleRemoveTag(tag)}
                              />
                            </Tag.EndElement>
                          </Tag.Root>
                        ))}
                      </Flex>
                    )}
                  </Box>
                )}
                <Box>
                  <UserSelect
                    errors={errors}
                    users={users}
                    onSearch={async (text) => await getUsers({ name: text })}
                    onChange={handleWithSelectUser}
                  />
                  {errors?.user?.map((error, index) => (
                    <ErrorLabel key={index} text={error} />
                  ))}
                  {selectedUsers.length > 0 && (
                    <Flex gap={1} flexWrap="wrap" mt={2}>
                      {selectedUsers.map((user) => (
                        <Tag.Root key={user.id} size="lg" bg="argos.fg" color="grayscale" fontWeight={600}>
                          <Tag.Label>{user.name}</Tag.Label>
                          <Tag.EndElement>
                            <Tag.CloseTrigger
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleRemoveUser(user.id)}
                            />
                          </Tag.EndElement>
                        </Tag.Root>
                      ))}
                    </Flex>
                  )}
                </Box>
                {errors?.general && (
                  <Flex direction="column" my={4}>
                    {errors.general.map((error, index) => (
                      <ErrorLabel key={index} text={error} />
                    ))}
                  </Flex>
                )}
              </Dialog.Body>

              <Dialog.Footer>
                <Dialog.ActionTrigger asChild>
                  <Button variant="outline" disabled={loading}>
                    Cancelar
                  </Button>
                </Dialog.ActionTrigger>
                <Button bg="green.700" color="white" type="submit" loading={loading}>
                  Salvar
                </Button>
              </Dialog.Footer>
            </form>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default AddDialog;
